import { Link } from 'react-router-dom';
import { AnalyticsToggle } from '../components/AnalyticsToggle/AnalyticsToggle';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { BRAND_NAME } from '../branding/branding';
import './marketing.css';

/**
 * Static privacy policy. Plain language so a 10-year-old (and their
 * grown-up) can actually parse it. Concrete promises, no weasel
 * words. Last-updated stamp lives in source so a `git blame` shows
 * exactly when each section changed.
 */
const LAST_UPDATED = '2026-05-13';

export function PrivacyPage(): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader subBrand="Privacy" privacy="on-device" />
      <main className="bl-marketing bl-marketing--prose">
        <header className="bl-marketing__hero">
          <p className="bl-marketing__eyebrow">Privacy</p>
          <h1>What we do with your stuff</h1>
          <p className="bl-marketing__lead">
            Plain English. If something here doesn&rsquo;t match what actually happens in the app,
            that&rsquo;s a bug — let us know on <Link to="/contact">the contact page</Link>.
          </p>
          <p className="bl-marketing__updated">Last updated {LAST_UPDATED}.</p>
        </header>

        <section className="bl-marketing__section">
          <h2>What stays on your device</h2>
          <ul>
            <li>The pictures and audio you record to train your model.</li>
            <li>The trained model itself, while you are training and testing in the studio.</li>
            <li>Block programs you write in the behavior editor, unless you choose to sync.</li>
          </ul>
          <p>
            These stay in your browser unless you click an action that sends them somewhere, like{' '}
            <em>Sync</em>, <em>Publish to gallery</em>, or <em>Ship</em>. Closing the tab keeps
            local projects; clearing browser data wipes them.
          </p>
        </section>

        <section className="bl-marketing__section">
          <h2>What we keep on our servers</h2>
          <ul>
            <li>Your account email and password hash (no plaintext passwords — ever).</li>
            <li>
              Project names, project kind, and block programs you sync, so you can switch devices
              without losing your work. Sync does not upload your training photos, audio, or model
              weights.
            </li>
            <li>
              Anything you explicitly publish to the gallery: one representative sample per class
              for safety review, plus the project&rsquo;s name and labels. Other samples stay on
              your device.
            </li>
            <li>
              Anything you explicitly ship as a live app: the page settings, labels, and trained
              model files the live page needs. Your training photos and clips stay on your device.
            </li>
          </ul>
        </section>

        <section className="bl-marketing__section">
          <h2>Kids under 13</h2>
          <p>
            Publishing to the public gallery requires parental consent. We email a magic link to the
            parent; they click <em>yes, share it</em>, and only then does {BRAND_NAME}&rsquo;s
            safety review run. If they say no, the project stays on the device.
          </p>
          <p>
            If you&rsquo;re a parent and want a copy of, or want us to delete, your child&rsquo;s
            data, please <Link to="/contact">contact us</Link>.
          </p>
        </section>

        <section className="bl-marketing__section">
          <h2>Cookies</h2>
          <p>
            For sign-in, we use one cookie: <code>bryta_session</code>. It keeps you signed in. We
            do not use ad networks or third-party tracking cookies.
          </p>
        </section>

        <section className="bl-marketing__section">
          <h2>Anonymous usage stats</h2>
          <p>
            We count things to learn what works and what gets people stuck — like how many kids
            train a model, how long training takes, which export buttons get clicked. The data is
            anonymous (a random ID in your browser, no name or email), and we never send pictures,
            sounds, class labels, project titles, or anything you typed. The stats go through a
            request on this site so ad-blockers see them as part of the app, not a third-party
            tracker.
          </p>
          <p>
            You can turn browser-side usage stats off any time. It only affects this device. We may
            still record server-side safety events for gallery moderation, like whether a submitted
            project was approved, rejected, or withdrawn.
          </p>
          <AnalyticsToggle />
        </section>

        <section className="bl-marketing__section">
          <h2>Account deletion</h2>
          <p>
            Click <strong>Delete my account</strong> from the account menu. The account row and
            related database rows are deleted, including sessions, synced projects, server-side
            sample records, submissions, and published-app records. Some backing files may be
            cleaned up separately if they were already written to object storage or live-app
            storage.
          </p>
        </section>

        <section className="bl-marketing__section">
          <h2>Contact</h2>
          <p>
            Questions, complaints, requests — the <Link to="/contact">contact page</Link> lands
            directly in the {BRAND_NAME} admin queue. A grown-up at {BRAND_NAME} reads every
            message.
          </p>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
